import { Menu as MenuIcon, Mic } from 'lucide-react';
import { NavLink } from 'react-router';
import { askAi } from '../../features/copilot';
import { cn } from '../../lib/cn';
import { useNavBadges } from '../../lib/nav-badges';
import { useAccess } from '../../lib/useAccess';
import { Badge } from '../../ui/Badge';
import { APPROVALS_NAV, MOBILE_NAV_LEFT, MOBILE_NAV_RIGHT, type NavItem } from './nav';
import { useOtherWorkspaces } from './other-workspaces';
import { useShell } from './shell-context';

/**
 * Dolne menu na mobile (< 768 px; DECISIONS 2026-10-17 [P] „Telefon: łatwy dostęp do całego menu”): Dziś, Inbox,
 * mikrofon (czarny, środkowy), Zadania, Menu. Inbox z liczbą nieprzeczytanych, Menu z liczbą akceptacji do decyzji
 * (Akceptacje są na górze panelu Menu) i kropką, gdy coś czeka w innym workspace'ie. „Menu” otwiera pełnoekranowy
 * panel ze wszystkimi modułami (`MobileMenu`, AppShell). Mikrofon (10.10.2026, DECISIONS [S] „Mikrofon w dolnym menu
 * = Copilot”) otwiera czat Copilota — ten sam panel co bąbel AI, na telefonie pełny ekran — i od razu dyktuje do pola
 * z wysyłką po zakończeniu mówienia; dalej zwykły przepływ Copilota (plan → Wykonaj / Cofnij).
 */
export function MobileNav({ className }: { className?: string }) {
  const access = useAccess();
  const badges = useNavBadges();
  // Stałe 5 miejsc: pozycja bez dostępu zostawia puste miejsce, więc mikrofon jest zawsze na środku, a Menu z prawej.
  const slot = (item: NavItem) =>
    item.module === null || access.can(item.module) ? (
      <MobileNavLink key={item.key} item={item} badge={item.module ? badges[item.module] : undefined} />
    ) : (
      <span key={item.key} aria-hidden />
    );
  const approvals = access.can('approvals') ? (badges.approvals?.count ?? 0) : 0;

  return (
    <nav
      aria-label="Menu dolne"
      className={cn(
        'fixed inset-x-0 bottom-0 z-40 border-t border-line bg-canvas/95 backdrop-blur-sm safe-bottom',
        className,
      )}
    >
      <div className="mx-auto grid h-[76px] max-w-[480px] grid-cols-5 place-items-center px-2">
        {MOBILE_NAV_LEFT.map(slot)}
        <button
          type="button"
          onClick={() => askAi.open({ voice: true })}
          aria-label="Powiedz, co zrobić (głos)"
          className="inline-flex size-[50px] shrink-0 items-center justify-center rounded-full bg-mic text-on-mic shadow-modal transition-transform active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Mic size={22} strokeWidth={1.75} aria-hidden />
        </button>
        {MOBILE_NAV_RIGHT.map(slot)}
        <MobileMenuToggle approvals={approvals} />
      </div>
    </nav>
  );
}

const itemClass = cn(
  'flex min-h-11 w-16 flex-col items-center justify-center gap-1 rounded-lg py-1 text-caption font-medium transition-colors',
  'focus-visible:outline-2 focus-visible:outline-accent',
);

function CornerBadge({ kind, count }: { kind: 'attention' | 'unread'; count: number }) {
  if (kind === 'attention') {
    return (
      <Badge kind="attention" count={count} className="absolute -top-1.5 -right-2.5 h-4 min-w-4 px-1 text-[10px]" />
    );
  }
  // nieprzeczytane: spokojna ciemna plakietka (niebieska zostaje dla „do zrobienia” — akceptacje)
  return (
    <span
      aria-hidden
      className="absolute -top-1.5 -right-2.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-fg px-1 text-[10px] font-semibold text-fg-inverse tabular"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function MobileNavLink({
  item,
  badge,
}: {
  item: NavItem;
  badge?: { kind: 'muted' | 'unread' | 'attention'; count: number };
}) {
  const Icon = item.icon;
  const shown = badge && badge.count > 0 && badge.kind !== 'muted' ? badge : undefined;
  return (
    <NavLink to={item.to} className={({ isActive }) => cn(itemClass, isActive ? 'text-fg' : 'text-fg-muted')}>
      <span className="relative">
        <Icon size={22} strokeWidth={1.75} aria-hidden />
        {shown && <CornerBadge kind={shown.kind === 'attention' ? 'attention' : 'unread'} count={shown.count} />}
      </span>
      <span>
        {item.label}
        {shown && (
          <span className="sr-only">
            {shown.kind === 'attention' ? ', do zrobienia: ' : ', nowe: '}
            {shown.count}
          </span>
        )}
      </span>
    </NavLink>
  );
}

/** Przycisk „Menu”: pełnoekranowy panel ze wszystkimi modułami, workspace'ami i kontem (`MobileMenu`). */
function MobileMenuToggle({ approvals }: { approvals: number }) {
  const { openMobileMenu, mobileMenuOpen } = useShell();
  // 8.10: kropka, gdy w innym workspace'ie osoby coś czeka (liczby w panelu przy workspace'ach)
  const waiting = useOtherWorkspaces().data.totalWaiting;
  // nazwa jawnie (bez spacji przed przecinkiem, którą przeglądarka wstawia przy elemencie sr-only)
  const name = [
    'Menu',
    approvals > 0 ? `, ${APPROVALS_NAV.label.toLowerCase()} do decyzji: ${approvals}` : '',
    waiting > 0 ? `, w innych workspace'ach czeka: ${waiting}` : '',
  ].join('');
  return (
    <button
      type="button"
      onClick={openMobileMenu}
      aria-label={name}
      aria-haspopup="dialog"
      aria-expanded={mobileMenuOpen ?? false}
      className={cn(itemClass, mobileMenuOpen ? 'text-fg' : 'text-fg-muted')}
    >
      <span className="relative">
        <MenuIcon size={22} strokeWidth={1.75} aria-hidden />
        {approvals > 0 ? (
          <CornerBadge kind="attention" count={approvals} />
        ) : (
          waiting > 0 && (
            <span
              aria-hidden
              className="absolute -top-0.5 -right-1 size-2.5 rounded-full bg-accent ring-2 ring-canvas"
            />
          )
        )}
      </span>
      <span aria-hidden>Menu</span>
    </button>
  );
}
